import { useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Billings.module.css";
import CreateInvoiceModal from "./CreateInvoiceModal";
import {
    FaFileInvoiceDollar,
    FaMoneyBillWave,
    FaWallet,
    FaCoins,
    FaUndo,
    FaArrowUp,
    FaArrowDown,
    FaPlus,
    FaSearch,
    FaFilter,
    FaEye,
    FaPrint,
    FaChevronLeft,
    FaChevronRight,
    FaTimes,
    FaSpinner,
    FaSave,
    FaCashRegister,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

/* ===========================
   SKELETON LOADER COMPONENTS
=========================== */
const SkeletonStatCard = () => (
    <div className={classes.skeletonStatCard}>
        <div className={classes.skeletonIconBox}></div>
        <div className={classes.skeletonStatContent}>
            <div className={classes.skeletonText} style={{ width: "60%" }}></div>
            <div
                className={classes.skeletonText}
                style={{ width: "80%", height: "22px" }}
            ></div>
            <div className={classes.skeletonText} style={{ width: "45%" }}></div>
        </div>
    </div>
);

const SkeletonRow = ({ cols = 9 }) => (
    <tr className={classes.skeletonRow}>
        {Array(cols)
            .fill(0)
            .map((_, i) => (
                <td key={i}>
                    <div
                        className={classes.skeletonText}
                        style={{ width: "80%" }}
                    ></div>
                </td>
            ))}
    </tr>
);

/* ===========================
   FALLBACK STATS
=========================== */
const fallbackStats = [
    {
        title: "Invoices Today",
        value: "0",
        change: "Today",
        direction: "up",
        color: "#2563EB",
        bg: "#EFF6FF",
        icon: <FaFileInvoiceDollar />,
    },
    {
        title: "Total Invoiced",
        value: "₦0",
        change: "All time",
        direction: "up",
        color: "#16A34A",
        bg: "#ECFDF5",
        icon: <FaMoneyBillWave />,
    },
    {
        title: "Total Paid",
        value: "₦0",
        change: "All time",
        direction: "up",
        color: "#7C3AED",
        bg: "#F5F3FF",
        icon: <FaWallet />,
    },
    {
        title: "Outstanding",
        value: "₦0",
        change: "All time",
        direction: "down",
        color: "#F59E0B",
        bg: "#FFF7ED",
        icon: <FaCoins />,
    },
    {
        title: "Refunds Today",
        value: "₦0",
        change: "Today",
        direction: "down",
        color: "#EF4444",
        bg: "#FEF2F2",
        icon: <FaUndo />,
    },
];

const tabs = ["Invoices", "Payments", "Refunds"];

/* ===========================
   HELPERS
=========================== */
const safeId = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "object") return val.id ?? null;
    return val;
};

const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return dateString;
    }
};

const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    try {
        const date = new Date(dateString);
        return date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return dateString;
    }
};

const formatNaira = (amount) => {
    const num = Number(amount || 0);
    return `₦${num.toLocaleString("en-NG")}`;
};

const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "paid") return classes.paid;
    if (s === "partial" || s === "partially_paid") return classes.partial;
    if (s === "refunded") return classes.unpaid;
    return classes.unpaid;
};

/* ===========================
   STATUS OPTIONS PER TAB
=========================== */
const STATUS_OPTIONS = {
    Invoices: ["All", "Paid", "Partial", "Unpaid"],
    Payments: ["All", "Paid", "Partial"],
    Refunds: ["All", "Approved", "Pending", "Rejected"],
};

const SEARCH_PLACEHOLDER = {
    Invoices: "Search Invoice, Patient...",
    Payments: "Search Payment, Patient...",
    Refunds: "Search Refund, Patient...",
};

/* ===========================
   CONSULTATION PAYMENT MODAL
=========================== */
const PAYMENT_METHODS = [
    "Cash",
    "Card",
    "Bank Transfer",
    "POS",
    "Cheque",
    "Mobile Money",
    "Insurance",
];

const ConsultationPaymentModal = ({ isOpen, onClose, onSaved }) => {
    const [consultations, setConsultations] = useState([]);
    const [isLoadingConsultations, setIsLoadingConsultations] =
        useState(false);
    const [selectedConsultation, setSelectedConsultation] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const [amount, setAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    // Reset state every time the modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedConsultation(null);
            setAmount("");
            setPaymentMethod("Cash");
            setFormError("");
            setSearchTerm("");
            fetchUnpaidConsultations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // ---------------------------------------------------------------
    // Fetch unpaid consultations from /consultation_register/unpaid
    // ---------------------------------------------------------------
    const fetchUnpaidConsultations = async () => {
        setIsLoadingConsultations(true);
        try {
            const companyId = parseInt(
                localStorage.getItem("companyId") || "1"
            );

            const response = await axios.get(
                `${BASE_URL}/api/consultation_register/unpaid`,
                {
                    params: { company_id: companyId },
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                            "authToken"
                        )}`,
                    },
                }
            );

            console.log("Unpaid consultations response:", response.data);

            const payload = response.data?.data || response.data || {};
            let list = [];

            if (Array.isArray(payload)) list = payload;
            else if (Array.isArray(payload.data)) list = payload.data;
            else if (Array.isArray(payload.patients)) list = payload.patients;
            else if (Array.isArray(payload.list)) list = payload.list;
            else if (payload.list && Array.isArray(payload.list.data))
                list = payload.list.data;
            else if (Array.isArray(payload.consultations))
                list = payload.consultations;
            else if (
                payload.consultations &&
                Array.isArray(payload.consultations.data)
            )
                list = payload.consultations.data;

            // Normalize each consultation
            const normalized = list.map((c) => {
                const consultation = c.consultation || c;
                const patient = c.patient || consultation.patient || {};
                const invoice =
                    c.invoice || consultation.invoice || c.bill || {};

                // Amounts can live at multiple places; take the first
                // non-null one.
                const total = Number(
                    invoice.total_amount ??
                        consultation.total_amount ??
                        c.total_amount ??
                        consultation.consultation_fee ??
                        0
                );
                const paid = Number(
                    invoice.amount_paid ??
                        consultation.amount_paid ??
                        c.amount_paid ??
                        0
                );
                const due = Number(
                    invoice.due_amount ??
                        invoice.balance ??
                        consultation.due_amount ??
                        c.due_amount ??
                        Math.max(total - paid, 0)
                );

                const first = patient.first_name || "";
                const middle = patient.middle_name || "";
                const last = patient.last_name || "";
                const patientName =
                    patient.name ||
                    `${first} ${middle} ${last}`
                        .replace(/\s+/g, " ")
                        .trim() ||
                    "Unnamed";

                // Consultation id is what make_payment expects (id param)
                const consultationId =
                    (typeof consultation.id === "number" &&
                        consultation.id) ??
                    c.consultation_id ??
                    c.id ??
                    null;

                // The PURPOSE lives on the invoice (e.g. "consultation"),
                // not the consultation type name.
                const purpose =
                    invoice.purpose ||
                    invoice.description ||
                    "consultation";

                return {
                    id: consultationId,
                    patientName,
                    patientNo:
                        patient.patient_no ||
                        patient.uhid ||
                        patient.patient_number ||
                        "—",
                    purpose,
                    invoiceNo: invoice.invoice_no || "—",
                    status:
                        consultation.status ||
                        c.status ||
                        "Unpaid",
                    invoiceStatus:
                        invoice.status || c.status || "Unpaid",
                    totalAmount: total,
                    amountPaid: paid,
                    dueAmount: due,
                    scheduleDate:
                        consultation.schedule_date || c.schedule_date,
                };
            });

            // Only keep rows with a usable id and outstanding balance
            const usable = normalized.filter(
                (c) => c.id !== null && c.dueAmount > 0
            );

            setConsultations(usable);
        } catch (err) {
            console.error("Error fetching unpaid consultations:", err);
            setConsultations([]);
        } finally {
            setIsLoadingConsultations(false);
        }
    };

    const filtered = consultations.filter((c) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return (
            c.patientName.toLowerCase().includes(term) ||
            c.patientNo.toLowerCase().includes(term) ||
            c.invoiceNo.toLowerCase().includes(term) ||
            c.purpose.toLowerCase().includes(term)
        );
    });

    const handleSelectConsultation = (c) => {
        setSelectedConsultation(c);
        // Pre-fill with the outstanding balance
        const suggested = c.dueAmount > 0 ? c.dueAmount : c.totalAmount;
        setAmount(suggested ? String(suggested) : "");
        setFormError("");
    };

    const handleSubmit = async () => {
        setFormError("");

        if (!selectedConsultation) {
            setFormError("Please select a consultation first.");
            return;
        }
        const amountNum = Number(amount);
        if (!amountNum || amountNum < 0.01) {
            setFormError("Amount must be at least 0.01.");
            return;
        }
        if (!paymentMethod || paymentMethod.length > 50) {
            setFormError(
                "Payment method is required and must be 50 characters or fewer."
            );
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                id: selectedConsultation.id,
                amount: amountNum,
                payment_method: paymentMethod,
            };

            console.log("Make payment payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/api/consultation_register/make_payment`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem(
                            "authToken"
                        )}`,
                    },
                }
            );

            console.log("Make payment response:", response.data);

            Swal.fire({
                icon: "success",
                title: "Payment Recorded",
                text: `${formatNaira(amountNum)} recorded for ${
                    selectedConsultation.patientName
                }.`,
                timer: 1500,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            console.error("Error recording consultation payment:", err);

            let msg = "Failed to record payment.";
            const data = err.response?.data;
            if (data?.message && typeof data.message === "object") {
                const firstKey = Object.keys(data.message)[0];
                const firstVal = data.message[firstKey];
                if (Array.isArray(firstVal) && firstVal[0]) msg = firstVal[0];
                else if (typeof firstVal === "string") msg = firstVal;
            } else if (data?.errors) {
                const errs = data.errors;
                const firstKey = Object.keys(errs)[0];
                msg = Array.isArray(errs[firstKey])
                    ? errs[firstKey][0]
                    : msg;
            } else if (typeof data?.message === "string") {
                msg = data.message;
            } else if (err.request) {
                msg = "Unable to connect to server.";
            }

            setFormError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={classes.paymentModalOverlay} onClick={onClose}>
            <div
                className={classes.paymentModalContent}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className={classes.paymentModalHeader}>
                    <div className={classes.paymentModalHeaderLeft}>
                        <div className={classes.paymentModalIcon}>
                            <FaCashRegister />
                        </div>
                        <div>
                            <h2>Consultation Payment</h2>
                            <p>Record a payment against an unpaid consultation</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={classes.paymentModalCloseBtn}
                        onClick={onClose}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* BODY */}
                <div className={classes.paymentModalBody}>
                    {/* LEFT: unpaid consultation list */}
                    <div className={classes.paymentLeft}>
                        <label className={classes.paymentFieldLabel}>
                            Select Unpaid Consultation
                        </label>
                        <div className={classes.paymentSearchBox}>
                            <FaSearch className={classes.paymentSearchIcon} />
                            <input
                                type="text"
                                placeholder="Search by patient name, UHID or invoice..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className={classes.paymentList}>
                            {isLoadingConsultations ? (
                                <div className={classes.paymentListLoading}>
                                    <FaSpinner className={classes.spin} />
                                    <span>Loading unpaid consultations...</span>
                                </div>
                            ) : filtered.length === 0 ? (
                                <div className={classes.paymentListEmpty}>
                                    No unpaid consultations found
                                </div>
                            ) : (
                                filtered.map((c) => (
                                    <div
                                        key={c.id}
                                        className={`${classes.paymentListRow} ${
                                            selectedConsultation?.id === c.id
                                                ? classes.paymentListRowSelected
                                                : ""
                                        }`}
                                        onClick={() =>
                                            handleSelectConsultation(c)
                                        }
                                    >
                                        <div
                                            className={
                                                classes.paymentListRowLeft
                                            }
                                        >
                                            <strong>{c.patientName}</strong>
                                            <span>{c.patientNo}</span>
                                            <span>
                                                {c.purpose} • {c.invoiceNo}
                                            </span>
                                        </div>
                                        <div
                                            className={
                                                classes.paymentListRowRight
                                            }
                                        >
                                            <span className={classes.amountDue}>
                                                Due:{" "}
                                                {formatNaira(c.dueAmount)}
                                            </span>
                                            <span className={classes.amountTotal}>
                                                Total:{" "}
                                                {formatNaira(c.totalAmount)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* RIGHT: form */}
                    <div className={classes.paymentRight}>
                        {!selectedConsultation ? (
                            <div className={classes.paymentEmptyRight}>
                                <FaCashRegister />
                                <p>
                                    Select an unpaid consultation on the left
                                    to record a payment
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className={classes.paymentSummaryBox}>
                                    <h4>
                                        {selectedConsultation.patientName}
                                    </h4>
                                    <p>
                                        {selectedConsultation.patientNo} •{" "}
                                        {selectedConsultation.invoiceNo}
                                    </p>
                                    <div
                                        className={
                                            classes.paymentSummaryAmounts
                                        }
                                    >
                                        <div>
                                            <span>Total</span>
                                            <strong>
                                                {formatNaira(
                                                    selectedConsultation.totalAmount
                                                )}
                                            </strong>
                                        </div>
                                        <div>
                                            <span>Paid</span>
                                            <strong>
                                                {formatNaira(
                                                    selectedConsultation.amountPaid
                                                )}
                                            </strong>
                                        </div>
                                        <div>
                                            <span>Outstanding</span>
                                            <strong>
                                                {formatNaira(
                                                    selectedConsultation.dueAmount
                                                )}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div className={classes.paymentField}>
                                    <label>Amount (₦)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={amount}
                                        onChange={(e) =>
                                            setAmount(e.target.value)
                                        }
                                        className={classes.paymentInput}
                                        placeholder="Enter amount"
                                    />
                                </div>

                                <div className={classes.paymentField}>
                                    <label>Payment Method</label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) =>
                                            setPaymentMethod(e.target.value)
                                        }
                                        className={classes.paymentInput}
                                    >
                                        {PAYMENT_METHODS.map((m) => (
                                            <option key={m} value={m}>
                                                {m}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {formError && (
                                    <div className={classes.paymentError}>
                                        {formError}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* FOOTER */}
                <div className={classes.paymentModalFooter}>
                    <button
                        type="button"
                        className={classes.paymentCancelBtn}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={classes.paymentSubmitBtn}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedConsultation}
                    >
                        {isSubmitting ? (
                            <>
                                <FaSpinner className={classes.spin} />{" "}
                                Recording...
                            </>
                        ) : (
                            <>
                                <FaSave /> Record Payment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

function Billings() {
    const [activeTab, setActiveTab] = useState("Invoices");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [refunds, setRefunds] = useState([]);
    const [summary, setSummary] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const rowsPerPage = 100;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
        setStatusFilter("All");
        setSearch("");
        setDebouncedSearch("");
    }, [activeTab]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, statusFilter]);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = parseInt(
                localStorage.getItem("companyId") || "1"
            );
            const params = { company_id: companyId, per_page: 100 };

            if (debouncedSearch?.trim())
                params.search = debouncedSearch.trim();
            if (statusFilter && statusFilter !== "All")
                params.status = statusFilter.toLowerCase();

            const response = await axios.get(`${BASE_URL}/api/invoice`, {
                params,
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem(
                        "authToken"
                    )}`,
                },
            });

            const payload = response.data?.data || {};
            const apiSummary = payload.summary || null;
            const paginated = payload.invoices || {};
            const list = Array.isArray(paginated.data)
                ? paginated.data
                : [];

            setInvoices(list);
            setSummary(apiSummary);
        } catch (err) {
            console.error("Error fetching invoices:", err);
            let msg = "Failed to load invoices.";
            if (err.response?.data?.message) {
                const m = err.response.data.message;
                msg =
                    typeof m === "string" ? m : "Failed to load invoices.";
            } else if (err.request) {
                msg = "Unable to connect to server.";
            }
            setError(msg);
            setInvoices([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, statusFilter]);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = parseInt(
                localStorage.getItem("companyId") || "1"
            );
            const params = { company_id: companyId, per_page: 100 };

            if (debouncedSearch?.trim())
                params.search = debouncedSearch.trim();

            const response = await axios.get(`${BASE_URL}/api/invoice`, {
                params,
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem(
                        "authToken"
                    )}`,
                },
            });

            const payload = response.data?.data || {};
            const paginated = payload.invoices || {};
            const list = Array.isArray(paginated.data)
                ? paginated.data
                : [];

            let filtered = list.filter(
                (inv) => Number(inv.amount_paid || 0) > 0 || inv.paid_at
            );

            if (statusFilter && statusFilter !== "All") {
                const target = statusFilter.toLowerCase();
                filtered = filtered.filter((inv) => {
                    const s = String(inv.status || "").toLowerCase();
                    if (target === "paid") return s === "paid";
                    if (target === "partial")
                        return s === "partial" || s === "partially_paid";
                    return true;
                });
            }

            setPayments(filtered);
        } catch (err) {
            console.error("Error fetching payments:", err);
            let msg = "Failed to load payments.";
            if (err.response?.data?.message) {
                const m = err.response.data.message;
                msg =
                    typeof m === "string" ? m : "Failed to load payments.";
            } else if (err.request) {
                msg = "Unable to connect to server.";
            }
            setError(msg);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, statusFilter]);

    const fetchRefunds = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            setRefunds([]);
        } catch (err) {
            console.error("Error fetching refunds:", err);
            setError("Failed to load refunds.");
            setRefunds([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === "Invoices") fetchInvoices();
        if (activeTab === "Payments") fetchPayments();
        if (activeTab === "Refunds") fetchRefunds();
    }, [activeTab, fetchInvoices, fetchPayments, fetchRefunds]);

    const statsData = useMemo(() => {
        if (!summary) return fallbackStats;

        return [
            {
                ...fallbackStats[0],
                value: summary.invoice_count?.toString() ?? "0",
            },
            {
                ...fallbackStats[1],
                value: formatNaira(summary.total_invoiced),
            },
            {
                ...fallbackStats[2],
                value: formatNaira(summary.total_paid),
            },
            {
                ...fallbackStats[3],
                value: formatNaira(summary.total_outstanding),
            },
            {
                ...fallbackStats[4],
                value: "₦0",
            },
        ];
    }, [summary]);

    const normalizedInvoices = useMemo(() => {
        return invoices.map((inv) => {
            const total = Number(inv.total_amount || 0);
            const paid = Number(inv.amount_paid || 0);
            const due = Number(
                inv.due_amount || Math.max(total - paid, 0)
            );

            return {
                id: safeId(inv.id),
                invoiceNo: inv.invoice_no || `INV-${inv.id}`,
                patient: inv.patient?.name || "Unnamed",
                patientId: inv.patient?.patient_no || "—",
                purpose: inv.purpose || inv.description || "—",
                totalAmount: total,
                paidAmount: paid,
                dueAmount: due,
                status: inv.status || "Unpaid",
                invoiceDate: formatDate(inv.invoice_date),
                dueDate: formatDate(inv.due_date),
                description: inv.description,
            };
        });
    }, [invoices]);

    const normalizedPayments = useMemo(() => {
        return payments.map((p) => ({
            id: safeId(p.id),
            invoiceNo: p.invoice_no || `INV-${p.id}`,
            patient: p.patient?.name || "Unnamed",
            patientId: p.patient?.patient_no || "—",
            purpose: p.purpose || p.description || "—",
            amountPaid: Number(p.amount_paid || 0),
            totalAmount: Number(p.total_amount || 0),
            dueAmount: Number(p.due_amount || 0),
            paymentMethod: p.payment_method || "—",
            status: p.status || "Paid",
            paidAt: formatDateTime(
                p.paid_at || p.updated_at || p.created_at
            ),
        }));
    }, [payments]);

    const normalizedRefunds = useMemo(() => {
        return refunds.map((r) => ({
            id: safeId(r.id),
            refundNo: r.refund_no || r.reference || `REF-${r.id}`,
            invoiceNo: r.invoice_no || r.invoice?.invoice_no || "—",
            patient: r.patient?.name || r.patient_name || "Unnamed",
            patientId: r.patient?.patient_no || "—",
            amount: Number(r.amount || 0),
            reason: r.reason || r.description || "—",
            status: r.status || "Pending",
            refundedAt: formatDateTime(r.refunded_at || r.created_at),
        }));
    }, [refunds]);

    const activeData = useMemo(() => {
        if (activeTab === "Invoices") return normalizedInvoices;
        if (activeTab === "Payments") return normalizedPayments;
        return normalizedRefunds;
    }, [activeTab, normalizedInvoices, normalizedPayments, normalizedRefunds]);

    const totalPages = Math.ceil(activeData.length / rowsPerPage);
    const currentRows = activeData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const handlePrevious = () => {
        if (currentPage > 1) setCurrentPage((prev) => prev - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
    };

    const handlePrint = () => window.print();

    const handleCreateInvoice = () => {
        setSelectedPatient({
            name: "John Doe",
            id: "QPO - 12345",
            age: "34 Y / Male",
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveInvoice = () => {
        setIsModalOpen(false);
        fetchInvoices();
    };

    const openPaymentModal = () => setIsPaymentModalOpen(true);
    const closePaymentModal = () => setIsPaymentModalOpen(false);
    const handlePaymentSaved = () => {
        if (activeTab === "Invoices") fetchInvoices();
        if (activeTab === "Payments") fetchPayments();
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    <div className={classes.topSection}>
                        <div>
                            <h2 className={classes.pageTitle}>
                                Billing &amp; Payments
                            </h2>
                            <p className={classes.breadcrumb}>
                                Dashboard <span> &gt; </span> Billing &amp;
                                Payments
                            </p>
                        </div>
                        <div className={classes.topButtons}>
                            <button
                                className={classes.consultationPaymentBtn}
                                onClick={openPaymentModal}
                            >
                                <FaCashRegister />
                                <span>Consultation Payment</span>
                            </button>
                            <button
                                className={classes.newBtn}
                                onClick={handleCreateInvoice}
                            >
                                <FaPlus />
                                <span>Create Invoice</span>
                            </button>
                            <button
                                className={classes.reportBtn}
                                onClick={handlePrint}
                            >
                                <FaPrint />
                                <span>Reports</span>
                            </button>
                        </div>
                    </div>

                    <div className={classes.statsGrid}>
                        {loading && activeTab === "Invoices"
                            ? Array(5)
                                  .fill(0)
                                  .map((_, i) => <SkeletonStatCard key={i} />)
                            : statsData.map((item, index) => (
                                  <div key={index} className={classes.statCard}>
                                      <div
                                          className={classes.iconBox}
                                          style={{
                                              background: item.bg,
                                              color: item.color,
                                          }}
                                      >
                                          {item.icon}
                                      </div>
                                      <div className={classes.statContent}>
                                          <p className={classes.statTitle}>
                                              {item.title}
                                          </p>
                                          <h2 className={classes.statValue}>
                                              {item.value}
                                          </h2>
                                          <div
                                              className={classes.statChange}
                                              style={{
                                                  color:
                                                      item.direction === "up"
                                                          ? "#16A34A"
                                                          : "#F59E0B",
                                              }}
                                          >
                                              {item.direction === "up" ? (
                                                  <FaArrowUp />
                                              ) : (
                                                  <FaArrowDown />
                                              )}
                                              <span>{item.change}</span>
                                          </div>
                                      </div>
                                  </div>
                              ))}
                    </div>

                    <div className={classes.tableHeader}>
                        <div className={classes.tabs}>
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={
                                        activeTab === tab
                                            ? classes.activeTab
                                            : classes.tab
                                    }
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                        <div className={classes.tableActions}>
                            <div className={classes.searchBox}>
                                <FaSearch />
                                <input
                                    type="text"
                                    placeholder={SEARCH_PLACEHOLDER[activeTab]}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <select
                                className={classes.filterSelect}
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                }
                            >
                                {STATUS_OPTIONS[activeTab].map((opt) => (
                                    <option key={opt}>{opt}</option>
                                ))}
                            </select>
                            <button className={classes.filterBtn}>
                                <FaFilter />
                                <span>Filter</span>
                            </button>
                        </div>
                    </div>

                    {error && !loading && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button
                                onClick={() => {
                                    if (activeTab === "Invoices")
                                        fetchInvoices();
                                    if (activeTab === "Payments")
                                        fetchPayments();
                                    if (activeTab === "Refunds")
                                        fetchRefunds();
                                }}
                                className={classes.retryBtn}
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    <div className={classes.tableCard}>
                        {activeTab === "Invoices" && (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Invoice No.</th>
                                        <th>Patient Name</th>
                                        <th>Date</th>
                                        <th>Purpose</th>
                                        <th>Total Amount</th>
                                        <th>Paid Amount</th>
                                        <th>Due Amount</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array(6)
                                            .fill(0)
                                            .map((_, i) => (
                                                <SkeletonRow
                                                    key={i}
                                                    cols={9}
                                                />
                                            ))
                                    ) : currentRows.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="9"
                                                className={classes.tableEmpty}
                                            >
                                                No invoices found
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRows.map((bill, index) => (
                                            <tr key={bill.id ?? index}>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.invoiceNo
                                                        }
                                                    >
                                                        {bill.invoiceNo}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {bill.patient}
                                                    </strong>
                                                    <br />
                                                    <span
                                                        className={
                                                            classes.patientIdText
                                                        }
                                                    >
                                                        {bill.patientId}
                                                    </span>
                                                </td>
                                                <td>{bill.invoiceDate}</td>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.purposeCell
                                                        }
                                                    >
                                                        {bill.purpose}
                                                    </span>
                                                </td>
                                                <td>
                                                    {formatNaira(
                                                        bill.totalAmount
                                                    )}
                                                </td>
                                                <td>
                                                    {formatNaira(
                                                        bill.paidAmount
                                                    )}
                                                </td>
                                                <td>
                                                    {formatNaira(
                                                        bill.dueAmount
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${
                                                            classes.badge
                                                        } ${getStatusClass(
                                                            bill.status
                                                        )}`}
                                                    >
                                                        {bill.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div
                                                        className={
                                                            classes.actionIcons
                                                        }
                                                    >
                                                        <FaEye />
                                                        <FaPrint
                                                            onClick={
                                                                handlePrint
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}

                        {activeTab === "Payments" && (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Payment Ref</th>
                                        <th>Invoice No.</th>
                                        <th>Patient Name</th>
                                        <th>Purpose</th>
                                        <th>Amount Paid</th>
                                        <th>Payment Method</th>
                                        <th>Paid At</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array(6)
                                            .fill(0)
                                            .map((_, i) => (
                                                <SkeletonRow
                                                    key={i}
                                                    cols={9}
                                                />
                                            ))
                                    ) : currentRows.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="9"
                                                className={classes.tableEmpty}
                                            >
                                                No payments found
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRows.map((pay, index) => (
                                            <tr key={pay.id ?? index}>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.invoiceNo
                                                        }
                                                    >
                                                        PAY-
                                                        {pay.id ?? index + 1}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.invoiceNo
                                                        }
                                                    >
                                                        {pay.invoiceNo}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {pay.patient}
                                                    </strong>
                                                    <br />
                                                    <span
                                                        className={
                                                            classes.patientIdText
                                                        }
                                                    >
                                                        {pay.patientId}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.purposeCell
                                                        }
                                                    >
                                                        {pay.purpose}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {formatNaira(
                                                            pay.amountPaid
                                                        )}
                                                    </strong>
                                                </td>
                                                <td>{pay.paymentMethod}</td>
                                                <td>{pay.paidAt}</td>
                                                <td>
                                                    <span
                                                        className={`${
                                                            classes.badge
                                                        } ${classes.paid}`}
                                                    >
                                                        {pay.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div
                                                        className={
                                                            classes.actionIcons
                                                        }
                                                    >
                                                        <FaEye />
                                                        <FaPrint
                                                            onClick={
                                                                handlePrint
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}

                        {activeTab === "Refunds" && (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Refund No.</th>
                                        <th>Invoice No.</th>
                                        <th>Patient Name</th>
                                        <th>Amount</th>
                                        <th>Reason</th>
                                        <th>Status</th>
                                        <th>Refunded At</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array(6)
                                            .fill(0)
                                            .map((_, i) => (
                                                <SkeletonRow
                                                    key={i}
                                                    cols={8}
                                                />
                                            ))
                                    ) : currentRows.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan="8"
                                                className={classes.tableEmpty}
                                            >
                                                No refunds found
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRows.map((ref, index) => (
                                            <tr key={ref.id ?? index}>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.invoiceNo
                                                        }
                                                    >
                                                        {ref.refundNo}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.invoiceNo
                                                        }
                                                    >
                                                        {ref.invoiceNo}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {ref.patient}
                                                    </strong>
                                                    <br />
                                                    <span
                                                        className={
                                                            classes.patientIdText
                                                        }
                                                    >
                                                        {ref.patientId}
                                                    </span>
                                                </td>
                                                <td>
                                                    <strong>
                                                        {formatNaira(
                                                            ref.amount
                                                        )}
                                                    </strong>
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            classes.purposeCell
                                                        }
                                                    >
                                                        {ref.reason}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`${
                                                            classes.badge
                                                        } ${getStatusClass(
                                                            ref.status
                                                        )}`}
                                                    >
                                                        {ref.status}
                                                    </span>
                                                </td>
                                                <td>{ref.refundedAt}</td>
                                                <td>
                                                    <div
                                                        className={
                                                            classes.actionIcons
                                                        }
                                                    >
                                                        <FaEye />
                                                        <FaPrint
                                                            onClick={
                                                                handlePrint
                                                            }
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}

                        {!loading &&
                            activeData.length > 0 &&
                            totalPages > 1 && (
                                <div className={classes.pagination}>
                                    <span>
                                        Showing{" "}
                                        {(currentPage - 1) * rowsPerPage + 1}{" "}
                                        to{" "}
                                        {Math.min(
                                            currentPage * rowsPerPage,
                                            activeData.length
                                        )}{" "}
                                        of {activeData.length} entries
                                    </span>
                                    <div className={classes.pageButtons}>
                                        <button
                                            onClick={handlePrevious}
                                            disabled={currentPage === 1}
                                        >
                                            <FaChevronLeft />
                                        </button>
                                        {Array.from(
                                            { length: totalPages },
                                            (_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() =>
                                                        setCurrentPage(
                                                            index + 1
                                                        )
                                                    }
                                                    className={
                                                        currentPage ===
                                                        index + 1
                                                            ? classes.activePage
                                                            : ""
                                                    }
                                                >
                                                    {index + 1}
                                                </button>
                                            )
                                        )}
                                        <button
                                            onClick={handleNext}
                                            disabled={
                                                currentPage === totalPages
                                            }
                                        >
                                            <FaChevronRight />
                                        </button>
                                    </div>
                                </div>
                            )}
                    </div>
                </div>
            </div>

            <CreateInvoiceModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveInvoice}
                patientData={selectedPatient}
            />

            <ConsultationPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={closePaymentModal}
                onSaved={handlePaymentSaved}
            />
        </div>
    );
}

export default Billings;